import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { UserDTO } from '../models/users.dto';
import { NgxPermissionsService } from 'ngx-permissions';
import { BackofficeCompanyDetailDTO } from '../models/company-backoffice-detail.interface';
import { BackofficeUserService } from '../infrastructure/backoffice/backoffice-user.service';
import { LanguageService } from '@domains/shared/domain/application-service/language.service';
import { ThemeService } from '@domains/shared/domain/application-service/theme.service';
import { PageSizeService } from '@domains/shared/domain/application-service/page-size.service';

@Injectable({ providedIn: 'root' })
export class IdentityFacade {


  private getLoggedUserSubject = new BehaviorSubject<UserDTO | undefined>(undefined);
  public getLoggedUser$ = this.getLoggedUserSubject.asObservable();
  private getCurrentCompanySubject = new BehaviorSubject<BackofficeCompanyDetailDTO | undefined>(undefined);
  public getCurrentCompany$ = this.getCurrentCompanySubject.asObservable();

  constructor(
    private permissionService: NgxPermissionsService,
    private backofficeUserService: BackofficeUserService,
    // LanguageService se resolvuje až při použití, ne v konstruktoru. Tuhle facade si
    // bere TokenInterceptor, a LanguageService táhne TranslateService, který hned střílí
    // request na překlady — v konstruktoru by tím vznikl cyklus na HTTP_INTERCEPTORS (NG0200).
    private injector: Injector) {
    // Oprávnění se přepočítají, kdykoli se změní uživatel nebo firma. Jediný odběr
    // na celý život facade (je to root singleton), zavěšený rovnou na subjekty.
    //
    // Dřív to viselo na finalize() veřejných streamů, což se spouští při unsubscribe,
    // ne při emitu — takže se oprávnění přepočítávala při každém teardownu komponenty
    // a každé volání navíc zakládalo nový combineLatest odběr, který se neuklízel.
    combineLatest([this.getLoggedUserSubject, this.getCurrentCompanySubject]).subscribe({
      next: ([user]) => this.applyPermissions(user),
    });
  }

  /** Aktuálně přihlášená firma bez čekání na stream — pro jednorázové akce v UI. */
  public getCurrentCompany(): BackofficeCompanyDetailDTO | undefined {
    return this.getCurrentCompanySubject.value;
  }

  public setCurrentCompany(company: BackofficeCompanyDetailDTO): void {
    this.getCurrentCompanySubject.next(company);
  }

  public setCurrentUser(user?: UserDTO): void {
    this.getLoggedUserSubject.next(user);

    // Tohle je jediné místo, kudy uživatel do aplikace vstoupí — po loginu i po reloadu
    // s platným tokenem — takže se tu uplatní jazyk a motiv z účtu.
    //
    // Podmínka na isAuthenticated je podstatná: /Auth/UserInfo vrací i nepřihlášenému
    // plný objekt s výchozím jazykem, takže bez ní by anonymní odpověď přepsala jazyk
    // z odkazu i zapamatovanou volbu zpátky na češtinu.
    if (user?.isAuthenticated) {
      // Jazyk z účtu je jen výchozí hodnota pro zařízení, kde si uživatel nic nezvolil —
      // vlastní volbu vlaječkou nepřebíjí. Rozhoduje o tom LanguageService, ne tohle místo.
      this.injector.get(LanguageService).useFromAccount(user.languageIso);

      // Motiv se drží na účtu, aby volba přešla i na další zařízení. Do přihlášení
      // se aplikace řídí tím, co si pamatuje prohlížeč — po něm rozhoduje účet.
      this.injector.get(ThemeService).applyFromAccount(user.theme);

      // Totéž pro počet řádků v tabulkách — ať se volba veze s účtem, ne s počítačem.
      this.injector.get(PageSizeService).applyFromAccount(user.pageSize);
    }
  }

  public getCurrentUser(): UserDTO | undefined {
    return this.getLoggedUserSubject.getValue();
  }

  /** Nahraje oprávnění podle rolí přihlášeného uživatele. */
  private applyPermissions(user: UserDTO | undefined): void {
    this.permissionService.flushPermissions();

    if (user) {
      const perms = user.assignedRoles.map(x => x.name);
      this.permissionService.loadPermissions(perms);
    }
  }

  canEmailBeUsedToCreateNewAccount(email: string): Observable<boolean> {
    return this.backofficeUserService.canEmailBeUsedToCreateNewAccount(email);
  }

}
